import {
  BookOpen,
  CalendarCheck,
  CheckCheck,
  CreditCard,
  FileText,
  Repeat2,
  Sparkles,
} from 'lucide-react';

import type { JourneyStageId } from './content';
import {
  ChannelBadge,
  ChatBubble,
  FieldRow,
  NotificationCard,
  PipelineStrip,
  ScoreMeter,
  Sparkline,
  StatTile,
  WindowChrome,
} from './mockups';
import { STAGE_VIDEOS, USE_STAGE_VIDEOS } from './media';
import { MkPill } from './primitives';

/* ------------------------------------------------------------------ *
 * Swappable media surface
 *
 * The product story renders animated UI mock-ups by default. Video URLs live
 * in `media.ts` — that is the only file to edit when real recordings arrive.
 * ------------------------------------------------------------------ */

/* ------------------------------------------------------------------ *
 * Stage mock-ups
 * ------------------------------------------------------------------ */

function ConnectStage(): JSX.Element {
  return (
    <WindowChrome title="Channels" right={<MkPill tone="positive">2 connected</MkPill>}>
      <div className="grid gap-3 p-4 sm:grid-cols-2 sm:p-5">
        {(['whatsapp', 'instagram'] as const).map((channel) => (
          <div key={channel} className="mk-card rounded-xl p-4">
            <div className="flex items-center justify-between">
              <ChannelBadge channel={channel} withLabel />
              <MkPill tone="positive">
                <CheckCheck className="h-3 w-3" aria-hidden />
                Connected
              </MkPill>
            </div>
            <p className="mt-3 text-xs leading-relaxed text-[var(--mk-ink-muted)]">
              {channel === 'whatsapp'
                ? 'WhatsApp Business number verified. Incoming messages route to the shared inbox.'
                : 'Instagram DMs and story replies route to the same inbox and lead records.'}
            </p>
          </div>
        ))}
      </div>
      <div className="border-t border-[var(--mk-line)] px-4 py-3.5 sm:px-5">
        <p className="mb-2.5 text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--mk-ink-muted)]">
          One queue
        </p>
        <ul className="space-y-2">
          {[
            { channel: 'whatsapp' as const, name: 'Rohit Mehra', text: '3BHK interiors, budget?' },
            { channel: 'instagram' as const, name: 'Aditi S.', text: 'Do you ship to Pune?' },
            { channel: 'whatsapp' as const, name: 'Karan V.', text: 'Site visit this Saturday?' },
          ].map((thread) => (
            <li
              key={thread.name}
              className="flex items-center gap-3 rounded-lg border border-[var(--mk-line)] px-3 py-2"
            >
              <ChannelBadge channel={thread.channel} />
              <span className="text-xs font-semibold text-[var(--mk-ink)]">{thread.name}</span>
              <span className="truncate text-xs text-[var(--mk-ink-muted)]">{thread.text}</span>
            </li>
          ))}
        </ul>
      </div>
    </WindowChrome>
  );
}

function KnowledgeStage(): JSX.Element {
  const entries = [
    { title: 'Services and scope', meta: '12 items', tag: 'Services' },
    { title: 'Pricing rules and slabs', meta: '8 items', tag: 'Pricing' },
    { title: 'Delivery and warranty policy', meta: '5 items', tag: 'Policy' },
    { title: 'Frequently asked questions', meta: '24 items', tag: 'FAQ' },
  ];
  return (
    <WindowChrome
      title="Business knowledge"
      right={
        <MkPill tone="accent">
          <BookOpen className="h-3 w-3" aria-hidden />
          Indexed
        </MkPill>
      }
    >
      <ul className="divide-y divide-[var(--mk-line)]">
        {entries.map((entry) => (
          <li
            key={entry.title}
            className="flex items-center justify-between gap-4 px-4 py-3.5 sm:px-5"
          >
            <div className="min-w-0">
              <p className="truncate text-sm font-semibold text-[var(--mk-ink)]">{entry.title}</p>
              <p className="mt-0.5 text-[11px] text-[var(--mk-ink-muted)]">{entry.meta}</p>
            </div>
            <MkPill>{entry.tag}</MkPill>
          </li>
        ))}
      </ul>
      <div className="border-t border-[var(--mk-line)] px-4 py-3.5 text-xs leading-relaxed text-[var(--mk-ink-muted)] sm:px-5">
        The AI answers from this material only. Anything outside it is handed to your team.
      </div>
    </WindowChrome>
  );
}

function AiStage(): JSX.Element {
  return (
    <WindowChrome title="Conversation" right={<ChannelBadge channel="instagram" withLabel />}>
      <div className="flex flex-col gap-3.5 p-4 sm:p-5">
        <ChatBubble from="customer" meta="Aditi · 21:07">
          Are you open on Sunday, and do you deliver to Pune?
        </ChatBubble>
        <ChatBubble from="ai" meta="Vaanexa AI · 21:07">
          We are open Sunday, 11 AM to 6 PM, and we do deliver to Pune with a 3–5 day lead time.
          Would you like me to check availability for what you have in mind?
        </ChatBubble>
        <div className="flex flex-wrap items-center gap-2 pt-1">
          <MkPill tone="accent">
            <Sparkles className="h-3 w-3" aria-hidden />
            Answered from knowledge base
          </MkPill>
          <MkPill>Source: Delivery policy</MkPill>
          <MkPill>Replied in 4s</MkPill>
        </div>
      </div>
    </WindowChrome>
  );
}

function QualifyStage(): JSX.Element {
  return (
    <WindowChrome title="Lead record" right={<MkPill tone="positive">Qualified</MkPill>}>
      <div className="px-4 py-2 sm:px-5">
        <dl>
          <FieldRow label="Name" value="Aditi Sharma" />
          <FieldRow label="Requirement" value="Bulk order, delivery to Pune" />
          <FieldRow label="Industry" value="Retail" />
          <FieldRow label="Intent" value="High" tone="accent" />
          <FieldRow label="Owner" value="Unassigned" />
        </dl>
      </div>
      <div className="border-t border-[var(--mk-line)] px-4 py-4 sm:px-5">
        <ScoreMeter value={84} />
        <div className="mt-4">
          <PipelineStrip activeStage={1} />
        </div>
      </div>
    </WindowChrome>
  );
}

function ActionStage(): JSX.Element {
  return (
    <div className="flex flex-col gap-3">
      <WindowChrome title="Automation">
        <div className="space-y-2.5 p-4 sm:p-5">
          <NotificationCard
            icon={<CalendarCheck className="h-4 w-4" />}
            title="Appointment booked — Sat, 11:00 AM"
            body="Slot offered from live availability and confirmed inside the chat."
            tone="positive"
          />
          <NotificationCard
            icon={<FileText className="h-4 w-4" />}
            title="Quotation generated"
            body="Priced from your rules, sent for approval before it reaches the customer."
            tone="accent"
          />
          <NotificationCard
            icon={<CreditCard className="h-4 w-4" />}
            title="Payment link shared"
            body="Secure link delivered in the thread; status syncs back to the lead."
            tone="accent"
          />
          <NotificationCard
            icon={<Repeat2 className="h-4 w-4" />}
            title="Follow-up sequence running"
            body="Stops the moment the customer replies or the deal is marked won."
          />
        </div>
      </WindowChrome>
    </div>
  );
}

function AnalyticsStage(): JSX.Element {
  return (
    <WindowChrome title="Dashboard" right={<MkPill>Last 30 days</MkPill>}>
      <div className="grid grid-cols-2 gap-3 p-4 sm:grid-cols-4 sm:p-5">
        <StatTile label="Conversations" value="—" hint="Live from your inbox" />
        <StatTile label="Leads" value="—" hint="Captured automatically" />
        <StatTile label="Conversions" value="—" hint="Won opportunities" />
        <StatTile label="Revenue" value="—" hint="From closed deals" />
      </div>
      <div className="border-t border-[var(--mk-line)] px-4 py-4 sm:px-5">
        <div className="flex items-center justify-between">
          <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[var(--mk-ink-muted)]">
            Conversation to conversion
          </p>
          <MkPill tone="accent">Trend</MkPill>
        </div>
        <Sparkline className="mt-3 h-14 w-full" />
        <div className="mt-4">
          <PipelineStrip activeStage={3} />
        </div>
      </div>
      <p className="border-t border-[var(--mk-line)] px-4 py-3 text-[11px] text-[var(--mk-ink-muted)] sm:px-5">
        Figures shown as placeholders — your dashboard reports on your own workspace.
      </p>
    </WindowChrome>
  );
}

const STAGE_VISUALS: Record<JourneyStageId, () => JSX.Element> = {
  connect: ConnectStage,
  knowledge: KnowledgeStage,
  ai: AiStage,
  qualify: QualifyStage,
  action: ActionStage,
  analytics: AnalyticsStage,
};

/**
 * Renders one stage of the product story.
 *
 * Order of preference:
 *   1. A stage clip from `media.ts`, when USE_STAGE_VIDEOS is on and motion is
 *      allowed. Muted, silent, looping — never autoplays with sound.
 *   2. That clip's poster as a still, when video is not appropriate (mobile,
 *      reduced motion).
 *   3. The built-in animated mock-up.
 *
 * The video is wrapped in a fixed aspect ratio so the layout is reserved before
 * a single byte of media loads.
 */
export function ProductMockup({
  stageId,
  active,
  allowVideo = true,
}: {
  stageId: JourneyStageId;
  active: boolean;
  /** False on mobile and under reduced motion — the poster is used instead. */
  allowVideo?: boolean;
}): JSX.Element {
  const media = USE_STAGE_VIDEOS ? STAGE_VIDEOS[stageId] : undefined;

  if (media && allowVideo) {
    return (
      <div
        className="overflow-hidden rounded-2xl border border-[var(--mk-line)] bg-black"
        style={{ aspectRatio: media.aspect ?? '16 / 10' }}
      >
        <video
          className="h-full w-full object-cover"
          src={media.src}
          poster={media.poster}
          muted
          playsInline
          loop
          preload="none"
          autoPlay={active}
          aria-label={media.alt}
        />
      </div>
    );
  }

  if (media?.poster) {
    return (
      <img
        className="w-full rounded-2xl border border-[var(--mk-line)]"
        src={media.poster}
        alt={media.alt}
        loading="lazy"
        decoding="async"
        style={{ aspectRatio: media.aspect ?? '16 / 10' }}
      />
    );
  }

  const Visual = STAGE_VISUALS[stageId];
  return <Visual />;
}
